import { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button } from 'antd';
import i18n from '../../i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * True when the error looks like a chunk-load failure — i.e. the user has a
 * stale index.html cached and the hashed bundle it points to no longer exists
 * on the server. Auto-reloading once fetches a fresh index.html with the new
 * bundle URLs. The sessionStorage guard prevents an infinite reload loop in
 * the (rare) case the failure is something else that happens to match.
 */
const CHUNK_RELOAD_GUARD = 'cdh:chunk-reload-attempted';
function isChunkLoadError(err: Error): boolean {
  const msg = err.message || '';
  return (
    err.name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_GUARD)) {
      sessionStorage.setItem(CHUNK_RELOAD_GUARD, '1');
      window.location.reload();
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '24px'
        }}>
          <Result
            status="error"
            title={i18n.t('error.title')}
            subTitle={
              <div>
                <p>{i18n.t('error.subtitle')}</p>
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <pre style={{
                    textAlign: 'left',
                    background: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    marginTop: '16px',
                    fontSize: '12px'
                  }}>
                    {this.state.error.toString()}
                    {this.state.error.stack}
                  </pre>
                )}
              </div>
            }
            extra={[
              <Button type="primary" key="home" onClick={this.handleReset}>
                {i18n.t('error.go_home')}
              </Button>,
              <Button key="reload" onClick={() => window.location.reload()}>
                {i18n.t('error.reload')}
              </Button>,
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
